/**
 * @fileoverview 書類配信（陸上 → 船）
 * @description 陸上で事前作成した帳票PDF（乗船証明書・届出書など）を船へ配信する。
 *              船上では権限を持つ役職者（船長など）が船員アプリの「書類・印刷」で受け取り、
 *              船内プリンターで印刷する。配信ごとに印刷状況（未印刷/印刷済み）を確認できる。
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Send, Trash2, FileText, CheckCircle, Clock, Printer, Eye } from 'lucide-react';
import * as api from '../services/api';
import { usePager } from '../components/TableTools';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:3001/api';

interface Delivery {
  id: string;
  title: string;
  file_name: string | null;
  note: string | null;
  vessel_id: string | null;
  vessel_name: string | null;
  recipient_user_id: string | null;
  recipient_name: string | null;
  created_by_name: string | null;
  created_at: string;
  printed_at: string | null;
  printed_by_name: string | null;
  direction: string | null;   // 'to_crew'（陸上→船・配信） / 'to_shore'（船→陸上・受信）
}
interface Vessel { id: string; vessel_name: string }
interface CrewUser { id: string; user_name: string; vessel_name?: string | null }

function authHeaders(): Record<string, string> {
  const subdomain = (() => {
    const h = window.location.hostname;
    if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; }
    return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech';
  })();
  return { Authorization: `Bearer ${api.getToken() ?? ''}`, 'X-Company-Subdomain': subdomain, 'X-App-Type': 'admin' };
}

const fmtDt = (v?: string | null) => (v ? String(v).slice(0, 16).replace('T', ' ') : '-');

const DocumentsPage: React.FC = () => {
  const [rows, setRows] = useState<Delivery[]>([]);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [crew, setCrew] = useState<CrewUser[]>([]);
  const [loading, setLoading] = useState(true);

  // 配信フォーム（宛先は船・乗組員とも複数選択可）
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [vesselIds, setVesselIds] = useState<string[]>([]);
  const [recipientIds, setRecipientIds] = useState<string[]>([]);
  const [crewSearch, setCrewSearch] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const toggle = (arr: string[], set: (v: string[]) => void, id: string) => set(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<any>('/documents');
      setRows((res.data ?? []) as Delivery[]);
    } catch (e) { console.error('[Documents]', e); }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    (async () => {
      try {
        const [v, u] = await Promise.all([api.get<any>('/vessels'), api.get<any>('/users', { limit: '500' })]);
        setVessels((v.vessels ?? v.data ?? []) as Vessel[]);
        setCrew((u.users ?? u.data ?? []) as CrewUser[]);
      } catch { /* ignore */ }
    })();
  }, [load]);

  const send = async () => {
    if (!file) { alert('ファイル（PDF/画像）を選択してください'); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (title.trim()) fd.append('title', title.trim());
      vesselIds.forEach(v => fd.append('vessel_ids', v));
      recipientIds.forEach(r => fd.append('recipient_user_ids', r));
      if (note.trim()) fd.append('note', note.trim());
      const r = await fetch(`${API_BASE}/documents`, { method: 'POST', headers: authHeaders(), body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => null))?.error ?? '配信に失敗しました');
      setFile(null); setTitle(''); setNote(''); setVesselIds([]); setRecipientIds([]); setCrewSearch('');
      await load();
    } catch (e: any) { alert(e?.message ?? '配信に失敗しました'); }
    setSending(false);
  };

  const remove = async (d: Delivery) => {
    if (!window.confirm(`「${d.title}」を削除しますか？（船側の受信箱からも消えます）`)) return;
    try { await api.del(`/documents/${d.id}`); await load(); } catch { alert('削除に失敗しました'); }
  };

  const view = (d: Delivery) => {
    fetch(`${API_BASE}/documents/${d.id}/file`, { headers: authHeaders() })
      .then(r => (r.ok ? r.blob() : Promise.reject()))
      .then(b => { const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.target = '_blank'; a.click(); })
      .catch(() => alert('表示に失敗しました'));
  };

  const pager = usePager(rows, { defaultPageSize: 20, noun: '件' });

  const lblS: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 };

  return (
    <div className="page-wrap">
      <div className="top-bar">
        <div>
          <h1 className="page-title">書類のやり取り</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            陸上→船へファイル（PDF/画像）を配信し、船→陸上の提出書類（「船→陸上 受信」表示）も一覧で確認できます
          </p>
        </div>
      </div>

      {/* 配信フォーム */}
      <div className="card" style={{ padding: '16px 18px' }}>
        <div style={{ fontSize: 13.5, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>
          <Send size={14} style={{ verticalAlign: -2, marginRight: 6 }} />新しい書類を配信
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* ファイル・タイトル・メモ */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <div>
              <div style={lblS}>ファイル（PDF / 画像）*</div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: file ? '1.5px solid #86efac' : '1.5px dashed #7dd3fc', background: file ? '#f0fdf4' : '#e0f2fe', color: file ? '#15803d' : '#0369a1', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', maxWidth: 280 }}>
                <FileText size={14} style={{ flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {file ? file.name : 'ファイルを選択（PDF/PNG/JPEG）'}
                </span>
                <input type="file" accept=".pdf,.png,.jpg,.jpeg" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) { setFile(f); if (!title) setTitle(f.name.replace(/\.[a-z0-9]+$/i, '')); } e.target.value = ''; }} />
              </label>
            </div>
            <div>
              <div style={lblS}>表示タイトル</div>
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="例: 検査案内（8/20）"
                style={{ padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: 220 }} />
            </div>
            <div style={{ flex: '1 1 200px' }}>
              <div style={lblS}>メモ（任意）</div>
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="例: 8/20の検査時に提示してください"
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} />
            </div>
          </div>

          {/* 宛先の船（複数選択可） */}
          <div>
            <div style={lblS}>宛先の船（複数選択可・選択した船の船長／印刷権限者へ）</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {vessels.length === 0 && <span style={{ fontSize: 12, color: '#94a3b8' }}>船舶がありません</span>}
              {vessels.map(v => { const on = vesselIds.includes(v.id); return (
                <button key={v.id} type="button" onClick={() => toggle(vesselIds, setVesselIds, v.id)}
                  style={{ padding: '6px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', background: on ? '#4338ca' : '#fff', color: on ? '#fff' : '#334155', border: on ? '1.5px solid #4338ca' : '1.5px solid #e2e8f0' }}>
                  {on ? '✓ ' : ''}{v.vessel_name}
                </button>
              ); })}
            </div>
          </div>

          {/* 特定の乗組員（複数選択・任意） */}
          <div>
            <div style={lblS}>特定の乗組員へ（任意・複数選択可）</div>
            <input value={crewSearch} onChange={e => setCrewSearch(e.target.value)} placeholder="船員を検索"
              style={{ width: 220, maxWidth: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 12.5, marginBottom: 6 }} />
            <div style={{ maxHeight: 150, overflowY: 'auto', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: '4px 8px' }}>
              {crew.filter(c => !crewSearch || c.user_name.includes(crewSearch) || (c.vessel_name || '').includes(crewSearch)).map(c => (
                <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', fontSize: 12.5, cursor: 'pointer' }}>
                  <input type="checkbox" checked={recipientIds.includes(c.id)} onChange={() => toggle(recipientIds, setRecipientIds, c.id)} />
                  <span style={{ fontWeight: 600, color: '#334155' }}>{c.user_name}</span>
                  <span style={{ color: '#94a3b8', fontSize: 11 }}>{c.vessel_name || '陸上'}</span>
                </label>
              ))}
              {crew.length === 0 && <div style={{ fontSize: 12, color: '#94a3b8', padding: 6 }}>乗組員がいません</div>}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button className="btn-p" style={{ padding: '9px 20px', display: 'inline-flex', alignItems: 'center', gap: 6 }} disabled={sending || !file} onClick={send}>
              <Send size={14} />{sending ? '配信中…' : '配信する'}
            </button>
            <span style={{ fontSize: 11.5, color: '#94a3b8' }}>
              {vesselIds.length === 0 && recipientIds.length === 0 ? '宛先未選択（全船の権限者へ）' : `宛先: 船${vesselIds.length}・乗組員${recipientIds.length}`}
            </span>
          </div>
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
          PDF・PNG・JPEG を送れます。宛先を指定するとアプリの連絡にお知らせが届きます。船だけ選ぶと、その船の「書類の受取・印刷」権限を持つ役職者（例: 船長）へ届きます。
        </div>
      </div>

      {/* 配信一覧 */}
      {loading ? (
        <div className="card"><div className="empty" style={{ padding: 40 }}>読み込み中…</div></div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table>
            <thead><tr>
              <th>タイトル</th><th>宛先</th><th>メモ</th><th>配信者</th><th>配信日時</th><th>印刷状況</th><th>操作</th>
            </tr></thead>
            <tbody>
              {pager.pageRows.map(d => (
                <tr key={d.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FileText size={15} color={d.direction === 'to_shore' ? '#7c3aed' : '#dc2626'} style={{ flexShrink: 0 }} />
                      <div>
                        <div style={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          {d.direction === 'to_shore' && <span style={{ fontSize: 10, fontWeight: 800, color: '#6d28d9', background: '#ede9fe', borderRadius: 999, padding: '1px 7px' }}>船→陸上 受信</span>}
                          {d.title}
                        </div>
                        {d.file_name && <div style={{ fontSize: 10.5, color: '#94a3b8' }}>{d.file_name}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 12.5 }}>
                    {d.recipient_name
                      ? <span style={{ fontWeight: 600 }}>{d.recipient_name}</span>
                      : d.vessel_name
                        ? <span>{d.vessel_name}（権限者）</span>
                        : <span style={{ color: '#64748b' }}>全船（権限者）</span>}
                  </td>
                  <td style={{ fontSize: 12, color: '#64748b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.note ?? '-'}</td>
                  <td style={{ fontSize: 12.5 }}>{d.created_by_name ?? '-'}</td>
                  <td style={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>{fmtDt(d.created_at)}</td>
                  <td>
                    {d.printed_at ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 700, color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '2px 9px' }}>
                        <CheckCircle size={11} />印刷済み {fmtDt(d.printed_at)}{d.printed_by_name ? `（${d.printed_by_name}）` : ''}
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 700, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 999, padding: '2px 9px' }}>
                        <Clock size={11} />未印刷
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => view(d)} title="表示"
                        style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #bfdbfe', background: '#fff', color: '#2563eb', borderRadius: 6, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Eye size={12} />表示
                      </button>
                      <button onClick={() => remove(d)} title="削除"
                        style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #fecaca', background: '#fff', color: '#dc2626', borderRadius: 6, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Trash2 size={12} />削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={7} className="empty" style={{ textAlign: 'center', padding: 30 }}>
                  配信した書類はまだありません
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {!loading && rows.length > 0 && pager.renderBar('bottom')}

      <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 10 }}>
        <Printer size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
        船側では、船員アプリの「設定 → 書類・印刷」で受け取り、船内プリンター（Wi-Fi接続）へ印刷できます。
      </div>
    </div>
  );
};

export default DocumentsPage;
